import React from 'react';
import {
  Box,
  Collapse,
  Flex,
  Tooltip,
  useDisclosure,
  UseDisclosureReturn,
  useToast,
  Image,
} from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import { Network } from 'lucid-txpipe';
import { useEffect, useMemo, useState } from 'react';
import {
  assetToUnit,
  Balance,
  getWalletBalance,
  userHasEnoughBalance,
} from '../blockchain/client';
import { handleOrderCreation } from '../blockchain/client/interact';
import { IPortfolioState, MintModalBalances, WalletData } from '../types';
import { themeVariables } from '../theme';
import BuySellFees from './buy-sell-fees';
import BuySellInput from './buy-sell-input';
import StepButtons from './step-buttons';
import TokensBreakdown from './tokens-breakdown';
import TransactionInProgressModal, {
  TransactionInProgress,
} from './transaction-in-progress';
import TransactionSubmittedModal, {
  TransactionSubmitted,
} from './transaction-submitted';
import useBuySellFees from './useBuySellFees';
import MintBurnWrapper from './mint-burn-wrapper';
import { min } from '../utils';
import SwapBreakdown from './swap-breakdown';
import { SettingsModal } from './settings-modal';
import { InfoIcon } from '@chakra-ui/icons';
import InsufficientTokens from './insufficient-tokens';

const settingsSRC =
  'https://sqvzfgobtlzbovqbkaax.supabase.co/storage/v1/object/public/sdk-assets//settings.svg';

interface IMintBurnProps {
  portfolio: IPortfolioState;
  type: 'mint' | 'burn';
  apiBaseUrl: string;
  network: Network;
  wallet?: WalletData | null;
  disc?: UseDisclosureReturn;
  containerProps?: Partial<{ width: string; height: string }>;
  background?: string;
  swapId?: string | null;
  primaryButtonColor?: string;
  hoverButtonColor?: string;
}

export function MintBurn({
  type: initialType,
  portfolio,
  disc,
  wallet,
  apiBaseUrl,
  background,
  containerProps = {},
  swapId = null,
  primaryButtonColor,
  hoverButtonColor,
}: IMintBurnProps) {
  const [inputValue, setInputValue] = useState<BigNumber>(BigNumber(0));
  const [balance, setBalance] = useState<Balance | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [type, setType] = useState<'mint' | 'burn'>(initialType);
  const toast = useToast();
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [swapDeficit, setSwapDeficit] = useState<boolean>(true);
  const [forceInsufficientToken, setForceInsufficientToken] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const isModal = disc !== undefined;

  const {
    amountsPerAsset,
    fees,
    formattedState,
    mintedMicroMTK,
    userReceiveMTK,
    withdrawLovelace,
    withdrawUserLvc,
  } = useBuySellFees({
    inputValue,
    portfolio,
    type,
  });

  const hasInsufficientToken = useMemo(
    () =>
      forceInsufficientToken ||
      (balance
        ? !userHasEnoughBalance(
            balance,
            amountsPerAsset.assets,
            type,
            inputValue,
            portfolio,
          )
        : true),
    [
      balance,
      amountsPerAsset.assets,
      type,
      inputValue,
      portfolio,
      forceInsufficientToken,
    ],
  );

  const progressDisc = useDisclosure({
    onClose: () => setInputValue(BigNumber(0)),
    isOpen: loading || !!txHash,
  });

  const swapBalances: MintModalBalances = useMemo(() => {
    const temp: MintModalBalances = {};
    for (const item of portfolio.assets) {
      const required = amountsPerAsset.assets[item.asset.id] || 0n;
      const owned = balance?.[assetToUnit(item.asset)] || 0n;
      const deficit = owned - required > 0n ? owned - required : 0n;
      temp[item.asset.id] = {
        required,
        asset: item.asset,
        owned,
        deficit,
      };
    }
    return temp;
  }, [balance, portfolio.assets, amountsPerAsset.assets]);

  useEffect(() => {
    if (!wallet) {
      return;
    }
    const getBalanceFromWallet = async () => {
      const newBalance = await getWalletBalance(wallet.wallet);
      setBalance(newBalance);
    };
    getBalanceFromWallet();
  }, [wallet]);

  const errorToast = (title: string, description: string) =>
    toast({
      title: title,
      description: description,
      status: 'error',
      isClosable: true,
      position: 'top-right',
    });

  const createTx = async () => {
    if (!wallet || !balance) {
      errorToast('Wallet not found', `Connect your wallet to ${type} tokens`);
      return;
    }
    if (hasInsufficientToken) {
      errorToast(
        'Not enough funds',
        `You don't have enough funds for this transaction`,
      );
      return;
    }

    setLoading(true);
    const amountToInteract =
      type === 'mint' ? inputValue : inputValue.negated();

    const expectedWorth = withdrawLovelace.abs().div(10 ** 6);
    if (type === 'mint' && inputValue.lt(0.017)) {
      errorToast(
        'Invalid amount',
        'The minimum amount you can deposit is 0.017 ADA',
      );
      setLoading(false);
      return;
    } else if (type === 'burn' && expectedWorth.lt(0.017)) {
      errorToast(
        'Invalid amount',
        'The minimum amount you can withdraw is 0.017 ADA',
      );
      setLoading(false);
      return;
    }

    await handleOrderCreation(
      wallet,
      portfolio,
      amountsPerAsset,
      amountToInteract,
      apiBaseUrl,
      swapId,
      {
        onError: (msg: string) => errorToast('Transaction error', msg),
        onSuccess: (txHash: string) => {
          toast({
            isClosable: true,
            status: 'success',
            title: 'Transaction submitted',
            description: 'Your order will be processed shortly',
          });
          setInputValue(BigNumber(0));
          setTxHash(txHash);
        },
      },
    );
    setLoading(false);
  };

  const handleSwapClick = () => {
    if (inputValue.gt(0)) {
      createTx();
    } else {
      toast({
        title: 'Invalid Input',
        description: `Enter a valid amount`,
        status: 'info',
        isClosable: true,
        duration: 1100,
      });
    }
  };

  const handleSettingsModal = () => {
    setSettingsModal(true);
  };

  return (
    <MintBurnWrapper background={background} {...containerProps}>
      {loading &&
        (isModal ? (
          <Box
            background={themeVariables.cards.background}
            height={'100%'}
            width={'100%'}
            padding="0rem"
            borderRadius={'1.25rem'}
            border={`1px solid ${themeVariables.cards.stroke}`}
            sx={{
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'border 0.2s, transform 0.2s',
              borderRadius: '10px !important',
              '--animated-border': themeVariables.animatedBorder.initialValue,
              background: `
            conic-gradient(
              from var(--animated-border),
              #171717a3,
              #171717a3 5%,
              #171717a3 60%,
              #171717a3 95%
            )
            padding-box,
            conic-gradient(
              from var(--animated-border),
              transparent 25%,
              #6d28d9,
              #a78bfa 99%,
              transparent
            )
            border-box`,
              animation: `${themeVariables.animatedBorder.bgSpin} 3s linear infinite`,
            }}
          >
            <TransactionInProgress
              title={hasInsufficientToken ? 'Zap-in in Progress' : undefined}
              isSwap={hasInsufficientToken}
              background={background}
            />
          </Box>
        ) : (
          <TransactionInProgressModal
            title={hasInsufficientToken ? 'Zap-in in Progress' : undefined}
            isSwap={hasInsufficientToken}
            disc={progressDisc}
            background={background}
          />
        ))}
      {!loading &&
        !!txHash &&
        (isModal ? (
          <TransactionSubmitted
            onClose={() => {
              setTxHash(null);
              disc && disc.onClose();
            }}
            title={hasInsufficientToken ? 'Zap-in Submitted' : undefined}
            description={
              hasInsufficientToken
                ? 'Your order is being handled externally. Navigate to the provider to obtain real-time updates'
                : undefined
            }
            route={hasInsufficientToken ? '/orders#swaps' : undefined}
            routeText={hasInsufficientToken ? 'Swap' : undefined}
            background={background}
          />
        ) : (
          <TransactionSubmittedModal
            disc={progressDisc}
            onClose={() => {
              setTxHash(null);
              disc && (disc as UseDisclosureReturn).onClose();
            }}
            title={hasInsufficientToken ? 'Zap-in Submitted' : undefined}
            description={
              hasInsufficientToken
                ? 'Your order is being handled externally. Navigate to the provider to obtain real-time updates'
                : undefined
            }
            route={hasInsufficientToken ? '/orders#swaps' : undefined}
            routeText={hasInsufficientToken ? 'Swap' : undefined}
            background={background}
          />
        ))}
      {!loading &&
        !txHash &&
        showBreakdown &&
        (hasInsufficientToken ? (
          <SwapBreakdown
            hideBreakdown={() => setShowBreakdown(false)}
            type={type}
            portfolioAssets={portfolio.assets}
            balance={balance}
            formattedState={formattedState}
            amountsToModify={amountsPerAsset.assets}
            isModal={isModal}
            setSwapDeficit={setSwapDeficit}
            swapDeficit={swapDeficit}
            background={background}
          />
        ) : (
          <TokensBreakdown
            to={type === 'mint' ? mintedMicroMTK : withdrawLovelace}
            from={inputValue}
            portfolioTicker={portfolio.portfolio.ticker}
            hideBreakdown={() => setShowBreakdown(false)}
            type={type}
            portfolioAssets={portfolio.assets}
            balance={balance}
            formattedState={formattedState}
            amountsToModify={amountsPerAsset.assets}
            hasInsufficientToken={hasInsufficientToken}
            isModal={isModal}
            background={background}
          />
        ))}
      {!showBreakdown && ((!loading && !txHash && isModal) || !isModal) && (
        <Flex flexDir={'column'} height="full" gap="1rem">
          <Flex justify="space-between">
            {type === 'mint' ? (
              <Flex width="full" align="center" gap={2}>
                <Flex
                  fontSize="1rem"
                  _hover={{ color: 'white' }}
                  cursor="pointer"
                  color={
                    forceInsufficientToken || hasInsufficientToken
                      ? '#a3a3a3'
                      : '#fafaf4'
                  }
                  fontWeight={400}
                  px="0.75rem"
                  py="0.375rem"
                  onClick={() => setForceInsufficientToken(false)}
                  gap={2}
                  align="center"
                >
                  <Box>Deposit Assets</Box>
                  <Tooltip label="Mint MTK shares by providing the underlying assets directly.">
                    <InfoIcon />
                  </Tooltip>
                </Flex>
                <Flex
                  fontSize="1rem"
                  _hover={{ color: 'white' }}
                  cursor="pointer"
                  color={hasInsufficientToken ? '#fafaf4' : '#a3a3a3'}
                  fontWeight={400}
                  px="0.75rem"
                  py="0.375rem"
                  onClick={() => {
                    if (!hasInsufficientToken) {
                      setForceInsufficientToken(true);
                      setSwapDeficit(false);
                    }
                  }}
                  gap={2}
                  align="center"
                >
                  <Box>Zap-in</Box>
                  <Tooltip label="Mint MTK shares by swapping ADA for the underlying assets via a DEX.">
                    <InfoIcon />
                  </Tooltip>
                </Flex>
              </Flex>
            ) : (
              <Flex width="full" align="center" gap={2}>
                <Flex
                  fontSize="1rem"
                  _hover={{ color: 'white' }}
                  cursor="pointer"
                  color="#fafaf4"
                  fontWeight={400}
                  px="0.75rem"
                  py="0.375rem"
                >
                  Receive Assets
                </Flex>
              </Flex>
            )}
            {type === 'mint' && hasInsufficientToken && (
              <>
                <Box
                  as="button"
                  onClick={handleSettingsModal}
                  flex="flex"
                  alignContent="center"
                  justifyContent="center"
                  borderRadius="50%" // Ensuring button is circular
                  bg="var(--bg-200)"
                  _hover={{
                    transform: 'scale(1.1)',
                  }}
                  p={2}
                >
                  <Image src={settingsSRC} alt="settings" />
                </Box>
                <SettingsModal
                  children={undefined}
                  isOpen={settingsModal}
                  onClose={() => setSettingsModal(false)}
                  hasInsufficientToken={hasInsufficientToken}
                  controls={{
                    SWAP_DEFICIT: {
                      value: swapDeficit,
                      setValue: setSwapDeficit,
                    },
                  }}
                />
              </>
            )}
          </Flex>
          <BuySellInput
            switchType={setType}
            balance={balance}
            setInputValue={setInputValue}
            mtkUnit={portfolio.portfolio.mtkUnit}
            portfolioImageUrl={portfolio.portfolio.imageUrl}
            withdrawLovelace={withdrawLovelace}
            expectedAmount={type === 'mint' ? mintedMicroMTK : withdrawLovelace}
            inputValue={inputValue}
            portfolioTicker={portfolio.portfolio.ticker}
            type={type}
            showBreakdown={() => setShowBreakdown(true)}
            hasInsufficientToken={hasInsufficientToken}
            portfolioAssets={portfolio.assets}
            background={background}
          />

          <Collapse
            in={inputValue.gt(0)}
            animateOpacity
            style={{ overflow: 'none' }}
          >
            <BuySellFees
              type={type}
              batcherFee={fees.batcherFee}
              managerFee={fees.userFee}
              platformFee={fees.platformFee}
              expectedAmount={userReceiveMTK}
              withdrawLovelace={withdrawUserLvc}
              mtkTicker={portfolio.portfolio.ticker}
              hasInsuficientToken={hasInsufficientToken}
            />
          </Collapse>
          {hasInsufficientToken && type === 'mint' ? (
            <InsufficientTokens
              wallet={wallet}
              apiBaseUrl={apiBaseUrl}
              setLoading={setLoading}
              setTxHash={setTxHash}
              portfolio={portfolio}
              totalInput={BigInt(inputValue.multipliedBy(10 ** 6).toFixed(0))}
              swapDeficit={swapDeficit}
              setSwapDeficit={setSwapDeficit}
              swapBalances={swapBalances}
            />
          ) : (
            <StepButtons
              poweredBy
              rightOnclick={handleSwapClick}
              rightText={type === 'mint' ? 'Buy' : 'Sell'}
              rightBtnProps={{
                maxH: '3rem',
                height: '100%',
                borderRadius: '62.5rem',
                padding: '1rem',
                width: '100%',
              }}
              primaryButtonColor={primaryButtonColor}
              hoverButtonColor={hoverButtonColor}
            />
          )}
        </Flex>
      )}
    </MintBurnWrapper>
  );
}

export default MintBurn;
